import { useEffect, useMemo, useRef, useState } from 'react';

import { Alert, Button, Pagination, Row, Space } from 'antd';

import useNotification from '@/hooks/notifications';
import useFetchListData from '../hooks/useFetchListData';
import HighlightedStreamingsTable from '../components/Tables/HighlightedStreamingsTable';
import BasicDatePicker from '../components/BasicComponents/BasicDatePicker';
import { dateToString, localTimeToUtc } from '../utils/dates';
import { highlightItemPost } from '../utils/fetchFunctions/highlight';

const timeOut = 500;

export default function SpotlightedLiveStream() {
	const {openNotification} = useNotification();
	const fetchTimeout = useRef(null);
	const [loadingData, setLoadingData] = useState(false);
	const [showInfoMessage, setShowInfoMessage] = useState(false);
	const params = {type: 'streamings', sort: 'mediaDate:asc,createDate:asc', pageSize: 50 };
	const {
		setParam,
		setParams,
		searchParams,
		error,
		data,
		pagination,
		loading,
		getData
	} = useFetchListData({params, newFunctions: true});

	const date = useMemo(() => {
		const today = dateToString(new Date()).slice(0, 8) + '000000';
		let date = searchParams.get("date") || today;

		if (Number(date) < Number(today)) {
			date = today;
		}

		setParam('date', date);

		return date;
	}, [searchParams]);
	const minDate = localTimeToUtc(new Date().setDate(new Date().getDate() - 1)).format();

	useEffect(() => {
		if (error) {
			openNotification({message: 'Something went wrong!', description: error.message || null, type: 'error'});
		}
	}, [error]);

	useEffect(() => {
		if (!searchParams.get("date")) {
			setParams({date});
		}
	}, []);

	const handlePagination = (page, pageSize) => {
		setParams({page, pageSize});
	};

	const handleDateChange = (date) => {
		setParams({date: dateToString(date)});
	};

	async function select (id) {
		setLoadingData(true);

		const errorMessage = timeCoincidence(data, id);

		if (errorMessage) {
			openNotification({message: errorMessage, type: 'error'});
			setLoadingData(false);
			return;
		}

		const res = await highlightItemPost({url: 'streamings', id});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingData(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingData(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	async function unselect (id) {
		setLoadingData(true);
		const res = await highlightItemPost({url: 'streamings', id, highlighted: false});

		if (res.error) {
			openNotification({message: 'Something went wrong!', description: res.message || null, type: 'error'});
			setLoadingData(false);
			return;
		}

		clearTimeout(fetchTimeout.current);
		fetchTimeout.current = setTimeout(async () => {
			await getData();
			setLoadingData(false);
			setShowInfoMessage(true);
		}, timeOut);
	}

	function timeCoincidence (liveStreamings, currentId) {
		const currentDate = new Date().getTime();
		const currentIndex = liveStreamings.findIndex(({epId}) => epId === currentId);
		const currentStreaming = liveStreamings[currentIndex];
		const currentStartDate = new Date(currentStreaming.liveStartDate).getTime();
		const currentEndDate = new Date(currentStreaming.liveEndDate).getTime();

		if (currentDate > currentEndDate) {
			return "The stream cannot be spotlighted because it has finished.";
		}

		const selectedLivestreamings = liveStreamings.filter(({selected, epId}) => selected && epId !== currentId);

		for (const streaming of selectedLivestreamings) {
			const streamingStartDate = new Date(streaming.liveStartDate).getTime();
			const streamingEndDate = new Date(streaming.liveEndDate).getTime();

			if (
				(streamingStartDate <= currentStartDate && streamingEndDate > currentStartDate) ||
				(streamingStartDate < currentEndDate && streamingEndDate >= currentEndDate) ||
				(streamingStartDate >= currentStartDate && streamingEndDate <= currentEndDate) ||
				(streamingStartDate > currentEndDate && streamingEndDate <= currentEndDate)
			) {
				return "The stream cannot be selected because it overlaps with another spotlighted stream.";
			}
		}

		return false;
	}

	return (
		<Space size="large" direction='vertical' style={{width: '100%'}} >
			<h1>{'Spotlighted live stream'}</h1>
			{
				showInfoMessage && (
					<Alert
						description={
							<Space size="large">
								<p>{"If you don't see the update after selecting the featured event, we recommend refreshing the page because the selected event is being indexed."}</p>
								<Button type="primary" key="back" onClick={getData}>
									Refresh
								</Button>
							</Space>
						}
						type="info"
					/>
				)
			}
			<>
				<BasicDatePicker
					title="Select a date:"
					value={localTimeToUtc(date).format()}
					minDate={minDate}
					showTime={false}
					format="DD-MM-YYYY"
					onChange={handleDateChange}
					style={{maxWidth: '400px'}}
					allowClear={false}
				/>
				<HighlightedStreamingsTable
					data={data}
					loading={loading || loadingData}
					select={select}
					unselect={unselect}
				/>
				<Row justify="center">
					<Pagination
						total={pagination.totalElements}
						pageSize={pagination.pageSize}
						current={pagination.page}
						showSizeChanger
						showQuickJumper
						hideOnSinglePage
						disabled={loading}
						onChange={handlePagination}
					/>
				</Row>
			</>
		</Space>
	);
}